import type { GetFinalizedStoryResponse } from "@api/types";
import { ArrowLeft, BookOpen, ExternalLink } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { StoryViewer } from "./StoryViewer";
import { Button } from "./ui/button";
import { CenteredCard } from "./ui/centered-card";

interface PublicStoryViewerProps {
    storyData: GetFinalizedStoryResponse | null;
}

export function PublicStoryViewer({ storyData }: PublicStoryViewerProps) {
    const posthog = usePostHog();

    if (!storyData) {
        return (
            <CenteredCard>
                <div className="text-center space-y-4">
                    <BookOpen className="h-12 w-12 text-muted-foreground mx-auto" />
                    <h2 className="text-2xl font-bold">Story Not Available</h2>
                    <p className="text-muted-foreground">This story may have been deleted or made private.</p>
                    <Button onClick={() => (window.location.href = "/")} variant="outline">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        <span className="pr-1">Go to</span>
                        <span className="text-primary">Story</span>
                        <span className="text-secondary">Doodle</span>
                    </Button>
                </div>
            </CenteredCard>
        );
    }

    const handleCreateStoryClick = () => {
        posthog?.capture("shared_story_cta_clicked", {
            story_title: storyData?.story?.title || storyData?.story?.topic,
            story_id: storyData?.story.id,
            conversion_source: "shared_story",
        });
        window.location.href = "/";
    };

    return (
        <div className="container mx-auto px-4 sm:px-6 py-8">
            <div className="max-w-4xl mx-auto">
                {/* Header with story title and brand */}
                <div className="text-center mb-8 space-y-4">
                    <h1 className="text-3xl sm:text-4xl font-bold text-primary">
                        {storyData.story.title || storyData.story.topic}
                    </h1>
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <span>A shared story from</span>
                        <a href="/" className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                            StoryDoodle
                            <ExternalLink className="h-3 w-3" />
                        </a>
                    </div>
                </div>

                {/* Shared story viewer */}
                <div className="mb-12">
                    <StoryViewer storyData={storyData} />
                </div>

                {/* Call to action */}
                <div className="text-center space-y-6 py-8 mb-8 md:mb-0 border-t">
                    <div className="space-y-2">
                        <h2 className="text-2xl font-bold">Create Your Own Story</h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Ready to embark on your own creative adventure? Start drawing and let AI bring your
                            imagination to life!
                        </p>
                    </div>
                    <Button onClick={handleCreateStoryClick} size="lg" className="gap-2">
                        <BookOpen className="h-4 w-4" />
                        Start Creating
                    </Button>
                </div>
            </div>
        </div>
    );
}
